'use client';

import { c as _c } from "react/compiler-runtime";
import { cx } from '@7f-ui';
import { BaseButton } from '@7f-ui/components/button/button';
import { Price } from '@7f-ui/components/price';
import { Skeleton } from '@7f-ui/components/skeleton/skeleton';
import { StickyFooter } from '@7f-ui/components/sticky-footer';
import { formatDateWithYearShort, useDateLabel } from '@7f-ui/hooks/use-date-label';
import type { AccommodationPricing } from '@data-access/graphql/schema.types';
import { useIsCartCurrencyMismatch } from '@feat/feat-cart/currency';
import { trackClickCta } from '@feat/feat-tracking/cta';
import { useUrlState } from '@feat/feat-url-state/use-url-state';
import { getNightCount } from '@utils/helpers';
import { shopTestId } from '@utils/utils-shop-test-ids';
import { useRouter } from 'next/navigation';
import { useTransition } from 'react';
import { HOTEL_ADS_NAV_ORDER } from 'src/lib/hotel-ads/nav';
import { useHotelAdsCriticalPath } from 'src/lib/hotel-ads/use-hotel-ads-critical-path';
import { getAccommodationErrorType, getAccommodationRoomsUrl } from '../_lib/accommodation-booking-box';
import { useAppAccommodationBookingBoxContext } from './app-accommodation-booking-box-provider';
type AppAccommodationBookingBoxMobileProps = {
  bestPrice?: AccommodationPricing | null;
  id: number;
  slug: string;
  currency: string;
  isLoading: boolean;
  isPending: boolean;
  isSingleRoom: boolean;
};
export function AppAccommodationBookingBoxMobile(t0) {
  const $ = _c(42);
  const {
    bestPrice,
    id,
    slug,
    isLoading,
    isPending,
    isSingleRoom,
    currency
  } = t0;
  const isCurrencyMismatch = useIsCartCurrencyMismatch(currency);
  const router = useRouter();
  const [isRouting, startRouting] = useTransition();
  const {
    urlState
  } = useUrlState();
  const {
    from,
    to
  } = urlState;
  let t1;
  if ($[0] !== from) {
    t1 = from ?? new Date();
    $[0] = from;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== to) {
    t2 = to ?? new Date();
    $[2] = to;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const nights = getNightCount(t1, t2);
  const {
    setActiveContent,
    setNavigateOnDateSubmit,
    setShowRoomRatesModal
  } = useAppAccommodationBookingBoxContext();
  const ctaLabel = isSingleRoom ? "In den Warenkorb" : "Zimmer w\xE4hlen";
  let t3;
  if ($[4] !== from || $[5] !== to) {
    t3 = {
      fromDate: from,
      toDate: to,
      formatFromOptions: formatDateWithYearShort,
      formatToOptions: formatDateWithYearShort,
      placeholder: "W\xE4hle einen Zeitraum"
    };
    $[4] = from;
    $[5] = to;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const dateLabel = useDateLabel(t3);
  const t4 = !!from && !!to;
  const t5 = isLoading || isPending;
  const t6 = !!bestPrice;
  let t7;
  if ($[7] !== t4 || $[8] !== t5 || $[9] !== t6) {
    t7 = getAccommodationErrorType(t4, t5, t6);
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const errorType = t7;
  const navAttributes = useHotelAdsCriticalPath(HOTEL_ADS_NAV_ORDER.startBooking, "mobile", !errorType);
  let t8;
  if ($[11] !== errorType || $[12] !== id || $[13] !== isSingleRoom || $[14] !== router || $[15] !== setActiveContent || $[16] !== setNavigateOnDateSubmit || $[17] !== setShowRoomRatesModal || $[18] !== slug) {
    t8 = () => {
      if (errorType === "no-date") {
        setActiveContent("Date");
        setNavigateOnDateSubmit(true);
        trackClickCta("calendar", {
          type: "accommodation"
        });
        return;
      }
      if (isSingleRoom && !errorType) {
        setShowRoomRatesModal(true);
        trackClickCta("to cart", {
          type: "accommodation"
        });
        return;
      }
      trackClickCta("choose rooms", {
        type: "accommodation"
      });
      startRouting(() => {
        router.push(getAccommodationRoomsUrl(id, slug));
      });
    };
    $[11] = errorType;
    $[12] = id;
    $[13] = isSingleRoom;
    $[14] = router;
    $[15] = setActiveContent;
    $[16] = setNavigateOnDateSubmit;
    $[17] = setShowRoomRatesModal;
    $[18] = slug;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const handleClick = t8;
  let t9;
  if ($[20] !== setActiveContent || $[21] !== setNavigateOnDateSubmit) {
    t9 = () => {
      setNavigateOnDateSubmit(false);
      setActiveContent("Date");
      trackClickCta("calendar", {
        type: "accommodation"
      });
    };
    $[20] = setActiveContent;
    $[21] = setNavigateOnDateSubmit;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const handleDateButtonClick = t9;
  let t10;
  if ($[23] !== bestPrice || $[24] !== errorType || $[25] !== nights) {
    t10 = () => {
      if (errorType === "no-date") {
        return "Preis anzeigen?";
      }
      if (errorType === "unavailable") {
        return <>Keine freien Zimmer.<br />Bitte Zeitraum ändern.</>;
      }
      return <>{bestPrice?.originalPrice ? <Price amount={bestPrice.originalPrice / nights} currency={bestPrice.currency} strikethrough={true} className="text-disabled-text" /> : undefined}{" ab "}<Price amount={bestPrice?.pricePerNight} currency={bestPrice?.currency} />{" pro Nacht"}</>;
    };
    $[23] = bestPrice;
    $[24] = errorType;
    $[25] = nights;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const title = t10;
  const t11 = errorType === "unavailable" || isCurrencyMismatch;
  let t12;
  if ($[27] !== ctaLabel || $[28] !== handleClick || $[29] !== isRouting || $[30] !== navAttributes || $[31] !== t11) {
    t12 = {
      children: ctaLabel,
      onClick: handleClick,
      disabled: t11,
      isLoading: isRouting,
      ...navAttributes
    };
    $[27] = ctaLabel;
    $[28] = handleClick;
    $[29] = isRouting;
    $[30] = navAttributes;
    $[31] = t11;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== dateLabel || $[34] !== errorType || $[35] !== handleDateButtonClick || $[36] !== isLoading || $[37] !== title) {
    t13 = isLoading ? <div className="flex flex-col gap-2"><Skeleton className="h-4 w-40" /><Skeleton className="h-4 w-32" /></div> : <><div className="font-base-copy text-primary" data-testid={shopTestId("AccommodationBookingBoxMobile.title")}>{title()}</div><BaseButton className={cx("font-body-small font-semibold underline underline-offset-2", {
        "text-error": errorType === "unavailable"
      })} onClick={handleDateButtonClick} data-testid={shopTestId("AccommodationBookingBox.dateButton")}>{dateLabel}</BaseButton></>;
    $[33] = dateLabel;
    $[34] = errorType;
    $[35] = handleDateButtonClick;
    $[36] = isLoading;
    $[37] = title;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== t12 || $[40] !== t13) {
    t14 = <StickyFooter buttonProps={t12}>{t13}</StickyFooter>;
    $[39] = t12;
    $[40] = t13;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  return t14;
}